import { useState, useEffect } from 'react'
import { Bus, Plus, Calendar, Clock, MapPin, User, X, RefreshCw, History, Trash2 } from 'lucide-react'
import { useSupabaseBusStore } from '../stores/supabaseBusStore'
import { useAuthStore } from '../stores/authStore'
import { TeamType } from '../../types'
import { supabase } from '../lib/supabase'

// Bus conflict validation types
interface TimeConflict {
  conflictingReservation: any;
  isPending: boolean;
}

// Add CSS animations
const addCSSAnimations = () => {
  if (typeof document !== 'undefined' && !document.querySelector('#bus-animations')) {
    const style = document.createElement('style')
    style.id = 'bus-animations'
    style.textContent = `
      @keyframes fadeInScale {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      
      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.7;
        }
      }
      
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      
      .bus-form-enter {
        animation: fadeInScale 0.3s ease-out;
      }
      
      .bus-field-enter {
        animation: slideInUp 0.2s ease-out;
      }
      
      .bus-loading {
        animation: pulse 1.5s ease-in-out infinite;
      }
    `
    document.head.appendChild(style)
  }
}

// Initialize animations
if (typeof document !== 'undefined') {
  addCSSAnimations()
}

// Helper function to convert time string to minutes
const timeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Bus conflict validation function
const checkBusConflicts = async (
  date: string,
  startTime: string,
  endTime: string
): Promise<TimeConflict[]> => {
  try {
    // Query existing reservations for the same date (approved + pending only)
    const { data: existingReservations, error } = await supabase
      .from('bus_reservations')
      .select('*')
      .eq('date', date)
      .in('status', ['approved', 'pending']);

    if (error) throw error;
    if (!existingReservations?.length) return [];

    const conflicts: TimeConflict[] = [];
    
    // Convert times to minutes for easier comparison
    const newStartMinutes = timeToMinutes(startTime);
    const newEndMinutes = endTime ? timeToMinutes(endTime) : newStartMinutes + 60; // Default 1h if no end time

    existingReservations.forEach(reservation => {
      const existingStartMinutes = timeToMinutes(reservation.start_time);
      const existingEndMinutes = reservation.end_time
        ? timeToMinutes(reservation.end_time)
        : existingStartMinutes + 60;

      // Check for time overlap: new start < existing end AND new end > existing start
      const hasOverlap = newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes;
      
      if (hasOverlap) {
        conflicts.push({
          conflictingReservation: reservation,
          isPending: reservation.status === 'pending'
        });
      }
    });

    return conflicts;
  } catch (error) {
    console.error('Error checking bus conflicts:', error);
    throw new Error('Fehler beim Prüfen der Bus-Verfügbarkeit');
  }
};

// Helper function to format time conflicts for user message
const formatConflictMessage = (conflicts: TimeConflict[], requestData: any): string => {
  const requestDate = new Date(requestData.departureDate).toLocaleDateString('de-CH', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  let message = `BUS-RESERVIERUNG KONFLIKT ERKANNT\n`;
  message += `=====================================\n\n`;
  message += `Angefragte Reservierung:\n`;
  message += `- Team: ${requestData.team}\n`;
  message += `- Zweck: ${requestData.purpose}\n`;
  message += `- Datum: ${requestDate}\n`;
  message += `- Zeit: ${requestData.startTime}${requestData.endTime ? ` - ${requestData.endTime}` : ''}\n`;
  message += `- Ziel: ${requestData.destination}\n`;
  message += `- Fahrer: ${requestData.driver}\n\n`;

  message += `Bestehende Reservierungen (Konflikt):\n`;
  message += `=====================================\n`;
  
  conflicts.forEach((conflict, index) => {
    const res = conflict.conflictingReservation;
    const startTime = res.start_time || res.startTime;
    const endTime = res.end_time || res.endTime;
    const conflictDate = new Date(res.date).toLocaleDateString('de-CH', {
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
    const statusText = conflict.isPending ? 'Ausstehend' : 'Genehmigt';
    
    message += `\n${index + 1}. Reservierung:\n`;
    message += `   - Team: ${res.team}\n`;
    message += `   - Zweck: ${res.purpose}\n`;
    message += `   - Datum: ${conflictDate}\n`;
    message += `   - Zeit: ${startTime}${endTime ? ` - ${endTime}` : ''}\n`;
    message += `   - Ziel: ${res.destination}\n`;
    message += `   - Fahrer: ${res.driver}\n`;
    message += `   - Status: ${statusText}\n`;
    if (res.notes && res.notes !== '-') {
      message += `   - Bemerkungen: ${res.notes}\n`;
    }
  });

  message += `\n=====================================\n`;
  message += `Bitte wählen Sie eine andere Zeit oder\n`;
  message += `koordinieren Sie sich mit den bestehenden\n`;
  message += `Reservierungen.\n\n`;
  message += `Kontakt: bus@fcfrick.ch\n`;
  message += `FC Frick Bus-Reservierungssystem\n`;
  message += `Generiert am: ${new Date().toLocaleString('de-CH')}`;

  return message;
};

const teams: TeamType[] = [
  '1. Mannschaft', '2. Mannschaft', '3. Mannschaft',
  '50+', '40+', '30+',
  'BA', 'BB', 'CA', 'CB', 'FF-14', 'FF-17',
  'D9A', 'D9B', 'D9C', 'D7A', 'D7B', 'D7C',
  'EA', 'EB', 'EC', 'ED', 'FA', 'FB', 'G'
]

export default function BusPage() {
  const { 
    reservations, 
    loading, 
    error, 
    fetchReservations, 
    addReservation,
    deleteReservation
  } = useSupabaseBusStore()
  const { isAuthenticated, profile, user } = useAuthStore()
  
  const [showForm, setShowForm] = useState(false)
  const [showPastReservations, setShowPastReservations] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [conflictError, setConflictError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    departureDate: '',
    returnDate: '',
    startTime: '06:00', // Default 6 AM in 24h format
    endTime: '18:00',   // Default 6 PM in 24h format
    destination: '',
    purpose: '',
    driver: '',
    contactPhone: '',
    team: '1. Mannschaft' as TeamType,
    notes: '-'
  })

  // Clear conflict error when form data changes (auto-dismiss popup)
  useEffect(() => {
    if (conflictError) {
      // Clear conflict error when critical fields change
      setConflictError(null)
    }
  }, [formData.departureDate, formData.startTime, formData.endTime]) // Only watch time/date changes

  useEffect(() => {
    console.log('BusPage useEffect - isAuthenticated:', isAuthenticated(), 'profile:', profile)
    
    // Ensure we have authentication before fetching
    const initializeData = async () => {
      if (isAuthenticated() && profile) {
        console.log('Fetching reservations...')
        try {
          await fetchReservations()
          console.log('Reservations fetched successfully')
        } catch (error) {
          console.error('Error fetching reservations:', error)
        }
      } else {
        console.log('Not authenticated or no profile, skipping fetch')
        // If not authenticated, try again after a short delay
        if (!isAuthenticated()) {
          console.log('Retrying authentication check in 1 second...')
          setTimeout(() => {
            if (isAuthenticated() && profile) {
              console.log('Authentication found on retry, fetching...')
              fetchReservations()
            }
          }, 1000)
        }
      }
    }
    
    initializeData()
  }, [isAuthenticated, profile, fetchReservations])

  // Force Swiss date format for date inputs
  useEffect(() => {
    // Set Swiss locale for date inputs
    const dateInputs = document.querySelectorAll('input[type="date"][lang="de-CH"]')
    dateInputs.forEach((input) => {
      const htmlInput = input as HTMLInputElement
      // Force Swiss date format display
      htmlInput.setAttribute('data-locale', 'de-CH')
    })
  }, [showForm]) // Re-run when form is shown

  const sendBusReservationEmail = async (reservationData: any) => {
    // Comment out old email functionality (Outlook opening)
    /*
    const formattedDate = new Date(reservationData.date).toLocaleDateString('de-DE', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
    
    const subject = `Neue Bus-Reservierung - ${formData.team}`
    
    const emailBody = `Hallo FC Frick Team,

eine neue Bus-Reservierung wurde über die App erstellt:

RESERVIERUNGSDETAILS:
Team: ${formData.team}
Datum: ${formattedDate}
Abfahrt: ${formData.startTime} Uhr
Rückfahrt: ${formData.endTime} Uhr${formData.returnDate ? ` (am ${new Date(formData.returnDate).toLocaleDateString('de-DE')})` : ''}
Ziel: ${formData.destination}
Zweck: ${formData.purpose}

FAHRER & KONTAKT:
Fahrer: ${formData.driver}
Kontakt-Telefon: ${formData.contactPhone}

${formData.notes ? `ZUSÄTZLICHE INFORMATIONEN:\n${formData.notes}` : ''}

Bitte bestätigt die Reservierung oder meldet euch bei Fragen.

Sportliche Grüsse
FC Frick App

---
Diese E-Mail wurde automatisch generiert.`

    // Send to bus@fcfrick.ch only
    const primaryEmail = 'bus@fcfrick.ch'
    
    const mailtoLink = `mailto:${primaryEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`
    
    window.location.href = mailtoLink
    */

    // New Netlify email function
    try {
      const { sendEmail, createBusEmail } = await import('../utils/emailService')
      const emailData = createBusEmail(reservationData, user?.email || '')
      const result = await sendEmail(emailData)
      
      if (!result.success) {
        console.error('Failed to send email:', result.error)
        // Could show user notification here if needed
      } else {
        console.log('Email sent successfully via Netlify function')
      }
    } catch (error) {
      console.error('Error sending email:', error)
    }
  }

  // Helper function to add hours to time string
  const addHoursToTime = (timeStr: string, hours: number): string => {
    if (!timeStr) return timeStr
    
    const [hoursPart, minutesPart] = timeStr.split(':').map(Number)
    let newHours = hoursPart + hours
    
    // Handle day overflow (24h format)
    if (newHours >= 24) {
      newHours = newHours - 24
    }
    if (newHours < 0) {
      newHours = 24 + newHours
    }
    
    return `${newHours.toString().padStart(2, '0')}:${minutesPart.toString().padStart(2, '0')}`
  }

  // Quick fix function for time conflicts
  const handleQuickTimeAdjust = () => {
    setFormData(prev => ({
      ...prev,
      startTime: addHoursToTime(prev.startTime, 1),
      endTime: addHoursToTime(prev.endTime, 1)
    }))
    setConflictError(null) // Clear error immediately
  }

  // Validation function for form data
  const validateFormData = (data: typeof formData): string | null => {
    // Check required fields
    if (!data.departureDate.trim()) {
      return 'Hinfahrt Datum ist erforderlich.'
    }
    if (!data.startTime.trim()) {
      return 'Hinfahrt Zeit ist erforderlich.'
    }
    if (!data.destination.trim()) {
      return 'Ziel ist erforderlich.'
    }
    if (!data.purpose.trim()) {
      return 'Zweck ist erforderlich.'
    }
    if (!data.driver.trim()) {
      return 'Fahrer ist erforderlich.'
    }
    if (!data.contactPhone.trim()) {
      return 'Kontakt-Telefon ist erforderlich.'
    }

    // Validate date format and logic
    const departureDate = new Date(data.departureDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (isNaN(departureDate.getTime())) {
      return 'Ungültiges Hinfahrt Datum.'
    }
    
    if (departureDate < today) {
      return 'Hinfahrt Datum kann nicht in der Vergangenheit liegen.'
    }

    // Validate return date if provided
    if (data.returnDate) {
      const returnDate = new Date(data.returnDate)
      if (isNaN(returnDate.getTime())) {
        return 'Ungültiges Rückfahrt Datum.'
      }
      if (returnDate < departureDate) {
        return 'Rückfahrt Datum kann nicht vor dem Hinfahrt Datum liegen.'
      }
    }

    // Validate time format
    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/
    if (!timeRegex.test(data.startTime)) {
      return 'Ungültige Hinfahrt Zeit. Format: HH:MM (00:00 - 23:59)'
    }

    if (data.endTime && !timeRegex.test(data.endTime)) {
      return 'Ungültige Rückfahrt Zeit. Format: HH:MM (00:00 - 23:59)'
    }

    // Validate time logic (only if same day)
    if (data.endTime && (!data.returnDate || data.returnDate === data.departureDate)) {
      const startMinutes = timeToMinutes(data.startTime)
      const endMinutes = timeToMinutes(data.endTime)
      
      if (endMinutes <= startMinutes) {
        return 'Rückfahrt Zeit muss nach der Hinfahrt Zeit liegen (am gleichen Tag).'
      }
    }

    // Validate phone number format (basic)
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,}$/
    if (!phoneRegex.test(data.contactPhone)) {
      return 'Ungültiges Telefonnummer Format.'
    }

    return null // No validation errors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🚌 Bus form submission started')
    
    // Set submitting state for visual feedback
    setIsSubmitting(true)
    
    // Clear any previous conflict errors
    setConflictError(null)
    
    try {
      // NEW: Validate form data first
      console.log('🔍 Validating form data...')
      const validationError = validateFormData(formData)
      
      if (validationError) {
        console.log('❌ Validation error:', validationError)
        setConflictError(`EINGABE-FEHLER\n================\n\n${validationError}\n\nBitte korrigieren Sie die Eingaben und versuchen Sie es erneut.`)
        
        // Scroll to top to show validation error
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
        
        setIsSubmitting(false);
        return; // Block submission
      }

      console.log('✅ Form validation passed')

      // NEW: Add conflict check before submission
      console.log('🔍 Checking for bus conflicts...')
      const conflicts = await checkBusConflicts(
        formData.departureDate,
        formData.startTime,
        formData.endTime || formData.startTime
      );

      if (conflicts.length > 0) {
        // Block submission and show conflict message
        const conflictMessage = formatConflictMessage(conflicts, formData);
        console.log('❌ Bus conflicts found:', conflicts)
        
        setConflictError(conflictMessage);
        
        // Scroll to top to show conflict error with smooth animation
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
        
        setIsSubmitting(false);
        return; // Block submission
      }

      console.log('✅ No bus conflicts found, proceeding with submission')

      const reservationData = {
        date: formData.departureDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        destination: formData.destination,
        purpose: formData.purpose,
        driver: formData.driver,
        contactPhone: formData.contactPhone,
        team: formData.team,
        notes: formData.notes ? `${formData.notes}${formData.returnDate ? ` | Rückfahrt: ${formData.returnDate}` : ''}` : formData.returnDate ? `Rückfahrt: ${formData.returnDate}` : ''
      }
      
      console.log('🚌 Reservation data:', reservationData)
      
      const result = await addReservation(reservationData)
      
      if (result.error) {
        console.error('❌ Bus reservation error:', result.error)
        setConflictError(`FEHLER BEIM ERSTELLEN\n================\n\nFehler beim Erstellen der Reservierung: ${result.error}\n\nBitte versuchen Sie es erneut oder kontaktieren Sie den Support.`)
        setIsSubmitting(false);
        return
      }
      
      console.log('✅ Bus reservation created successfully')
      
      // Send automated email
      await sendBusReservationEmail(reservationData)
      
      setFormData({
        departureDate: '',
        returnDate: '',
        startTime: '06:00', // Reset to default 6 AM
        endTime: '18:00',   // Reset to default 6 PM
        destination: '',
        purpose: '',
        driver: '',
        contactPhone: '',
        team: '1. Mannschaft' as TeamType,
        notes: '-'
      })
      setShowForm(false)
      setIsSubmitting(false);
      
      // Scroll to top with success feedback
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
      
      // Erfolgs-Feedback und Auto-Refresh
      setConflictError(`ERFOLG\n================\n\nBus-Reservierung erfolgreich erstellt und E-Mail versendet!\n\nIhre Reservierung wurde gespeichert und das FC Frick Team wurde benachrichtigt.`)
      
      // Daten nach kurzer Verzögerung neu laden
      setTimeout(() => {
        fetchReservations()
      }, 1000)
      
    } catch (err: any) {
      console.error('❌ Bus form submission error:', err)
      setConflictError(`SYSTEM-FEHLER\n================\n\nFehler: ${err.message}\n\nEin unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.`)
      setIsSubmitting(false);
    }
  }

  const handleDeleteReservation = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Reservierung löschen möchten?')) {
      return
    }

    console.log('🗑️ Deleting bus reservation:', id)
    setDeletingId(id)
    
    try {
      const result = await deleteReservation(id)
      
      if (result.error) {
        console.error('❌ Delete reservation error:', result.error)
        setConflictError(`FEHLER BEIM LÖSCHEN\n================\n\nFehler beim Löschen der Reservierung: ${result.error}\n\nBitte versuchen Sie es erneut oder kontaktieren Sie den Support.`)
        return
      }
      
      console.log('✅ Bus reservation deleted successfully')
      setConflictError(`ERFOLG\n================\n\nReservierung erfolgreich gelöscht!\n\nDie Reservierung wurde aus dem System entfernt.`)
      
    } catch (err: any) {
      console.error('❌ Delete reservation error:', err)
      setConflictError(`SYSTEM-FEHLER\n================\n\nFehler: ${err.message}\n\nEin unerwarteter Fehler beim Löschen ist aufgetreten. Bitte versuchen Sie es erneut.`)
    } finally {
      setDeletingId(null)
    }
  }

  // Filter reservations by date
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Start of today
  
  const currentAndFutureReservations = reservations.filter(reservation => {
    const reservationDate = new Date(reservation.date)
    reservationDate.setHours(0, 0, 0, 0)
    return reservationDate >= today
  })
  
  const pastReservations = reservations.filter(reservation => {
    const reservationDate = new Date(reservation.date)
    reservationDate.setHours(0, 0, 0, 0)
    return reservationDate < today
  })

  return (
    <>
      <div style={{ padding: '20px 0' }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
            letterSpacing: '-0.025em'
          }}>
            Bus-Reservierungen
          </h1>
          <p style={{
            color: '#6b7280',
            fontSize: '18px',
            fontWeight: '500'
          }}>
            Reserviere den Vereinsbus für Auswärtsfahrten und Veranstaltungen
          </p>
        </div>
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={() => {
              console.log('Refreshing reservations...')
              fetchReservations()
            }}
            style={{
              background: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              color: '#374151'
            }}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.borderColor = '#1B7E3A'
                e.currentTarget.style.backgroundColor = '#f0fdf4'
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <RefreshCw 
              size={16} 
              style={{
                animation: loading ? 'spin 1s linear infinite' : 'none'
              }}
            />
            Aktualisieren
          </button>
          <button 
            onClick={() => setShowForm(true)}
            style={{
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
          >
            <Plus size={16} />
            Neue Reservierung
          </button>
        </div>
      </div>

      {/* Bus Conflict Error - Positioned above form */}
      {conflictError && (
        <div style={{
          backgroundColor: conflictError.startsWith('ERFOLG') ? '#16a34a' : '#dc2626',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px',
          boxShadow: conflictError.startsWith('ERFOLG') 
            ? '0 10px 25px rgba(22, 163, 74, 0.3)' 
            : '0 10px 25px rgba(220, 38, 38, 0.3)',
          border: conflictError.startsWith('ERFOLG') 
            ? '2px solid #15803d' 
            : '2px solid #b91c1c',
          position: 'relative',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <span style={{ 
                  color: conflictError.startsWith('ERFOLG') ? '#16a34a' : '#dc2626', 
                  fontSize: '16px', 
                  fontWeight: 'bold' 
                }}>
                  {conflictError.startsWith('ERFOLG') ? '✓' : '!'}
                </span>
              </div>
              <h3 style={{ 
                fontWeight: '700', 
                fontSize: '18px',
                margin: '0'
              }}>
                {conflictError.startsWith('ERFOLG') 
                  ? 'ERFOLG: Aktion erfolgreich' 
                  : 'KONFLIKT: Bus-Reservierung nicht möglich'}
              </h3>
            </div>
            <button
              onClick={() => setConflictError(null)}
              style={{
                padding: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
              }}
              title="Schließen"
            >
              ×
            </button>
          </div>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '16px',
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.6',
            fontFamily: 'monospace',
            whiteSpace: 'pre-line',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            {conflictError}
          </div>
          <div style={{
            marginTop: '12px',
            fontSize: '13px',
            opacity: '0.9',
            fontStyle: 'italic'
          }}>
            💡 Tipp: Markieren Sie den obigen Text und kopieren Sie ihn für E-Mails oder Notizen
          </div>
          
          {/* Quick Fix Button - only show for conflicts, not success messages */}
          {!conflictError.startsWith('ERFOLG') && (
            <div style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleQuickTimeAdjust}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)'
                }}
                title="Automatisch +1 Stunde zu beiden Zeiten hinzufügen"
              >
                🕐 Zeit +1h anpassen
              </button>
              <span style={{
                fontSize: '12px',
                opacity: '0.8'
              }}>
                Schnelle Lösung: Zeiten um 1 Stunde verschieben
              </span>
            </div>
          )}
        </div>
      )}

      {/* New Reservation Form */}
      {showForm && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative elements */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #1B7E3A 0%, #16a34a 100%)'
          }} />
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(27, 126, 58, 0.1) 0%, rgba(22, 163, 74, 0.05) 100%)',
            opacity: '0.5'
          }} />
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              margin: '0'
            }}>Neue Bus-Reservierung</h2>
            <button 
              onClick={() => setShowForm(false)} 
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#dc2626'
                e.currentTarget.style.backgroundColor = '#fef2f2'
                e.currentTarget.style.color = '#dc2626'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = '#6b7280'
              }}
            >
              <X size={16} />
              Schließen
            </button>
          </div>
          
          <form onSubmit={handleSubmit} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Team *</label>
                <select
                  value={formData.team}
                  onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value as TeamType }))}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  required
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  {teams.map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Zweck *</label>
                <input
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  placeholder="z.B. Auswärtsspiel, Training"
                  required
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Hinfahrt Datum *</label>
                <input
                  type="date"
                  value={formData.departureDate}
                  onChange={(e) => {
                    const newDepartureDate = e.target.value
                    setFormData(prev => ({ ...prev, departureDate: newDepartureDate }))
                    
                    // If return date is set and now before departure date, clear it
                    if (formData.returnDate && formData.returnDate < newDepartureDate) {
                      setFormData(prev => ({ ...prev, returnDate: '' }))
                      setConflictError(`EINGABE-HINWEIS\n================\n\nRückfahrt Datum wurde zurückgesetzt, da es vor dem neuen Hinfahrt Datum lag.\n\nBitte wählen Sie ein neues Rückfahrt Datum.`)
                    }
                  }}
                  min={new Date().toISOString().split('T')[0]} // Prevent past dates
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  lang="de-CH"
                  data-date-format="dd.mm.yyyy"
                  required
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Rückfahrt Datum</label>
                <input
                  type="date"
                  value={formData.returnDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, returnDate: e.target.value }))}
                  min={formData.departureDate || new Date().toISOString().split('T')[0]} // Prevent dates before departure
                  onBlur={(e) => {
                    const returnDate = e.target.value
                    if (returnDate && formData.departureDate && returnDate < formData.departureDate) {
                      setConflictError(`EINGABE-FEHLER\n================\n\nRückfahrt Datum kann nicht vor dem Hinfahrt Datum liegen!\n\nHinfahrt Datum: ${formData.departureDate}\nRückfahrt Datum: ${returnDate}\n\nBitte korrigieren Sie das Datum.`)
                      e.target.focus()
                      return
                    }
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  lang="de-CH"
                  data-date-format="dd.mm.yyyy"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Hinfahrt Zeit (24h Format) *</label>
                <input
                  type="text"
                  value={formData.startTime}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d:]/g, '')
                    if (value.length === 2 && !value.includes(':')) {
                      value += ':'
                    }
                    if (value.length <= 5) {
                      setFormData(prev => ({ ...prev, startTime: value }))
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value
                    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/
                    if (value && !timeRegex.test(value)) {
                      setConflictError(`EINGABE-FEHLER\n================\n\nBitte geben Sie eine gültige Zeit im Format HH:MM ein (00:00 - 23:59)\n\nKorrigieren Sie die Hinfahrt Zeit und versuchen Sie es erneut.`)
                      e.target.focus()
                      return
                    }

                    // Check time logic if end time is also filled and same day
                    if (formData.endTime && (!formData.returnDate || formData.returnDate === formData.departureDate)) {
                      const startMinutes = timeToMinutes(value)
                      const endMinutes = timeToMinutes(formData.endTime)
                      
                      if (endMinutes <= startMinutes) {
                        setConflictError(`EINGABE-FEHLER\n================\n\nHinfahrt Zeit muss vor der Rückfahrt Zeit liegen (am gleichen Tag)!\n\nAktuelle Zeiten:\n- Hinfahrt: ${value}\n- Rückfahrt: ${formData.endTime}\n\nBitte korrigieren Sie die Zeiten.`)
                        e.target.focus()
                        return
                      }
                    }

                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  placeholder="HH:MM (z.B. 06:00, 14:30)"
                  title="24-Stunden Format: HH:MM (z.B. 06:00, 14:30)"
                  required
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Rückfahrt Zeit (24h Format)</label>
                <input
                  type="text"
                  value={formData.endTime}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d:]/g, '')
                    if (value.length === 2 && !value.includes(':')) {
                      value += ':'
                    }
                    if (value.length <= 5) {
                      setFormData(prev => ({ ...prev, endTime: value }))
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value
                    if (value) {
                      const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/
                      if (!timeRegex.test(value)) {
                        setConflictError(`EINGABE-FEHLER\n================\n\nBitte geben Sie eine gültige Zeit im Format HH:MM ein (00:00 - 23:59)\n\nKorrigieren Sie die Rückfahrt Zeit und versuchen Sie es erneut.`)
                        e.target.focus()
                        return
                      }

                      // Check time logic if start time is filled and same day
                      if (formData.startTime && (!formData.returnDate || formData.returnDate === formData.departureDate)) {
                        const startMinutes = timeToMinutes(formData.startTime)
                        const endMinutes = timeToMinutes(value)
                        
                        if (endMinutes <= startMinutes) {
                          setConflictError(`EINGABE-FEHLER\n================\n\nRückfahrt Zeit muss nach der Hinfahrt Zeit liegen (am gleichen Tag)!\n\nAktuelle Zeiten:\n- Hinfahrt: ${formData.startTime}\n- Rückfahrt: ${value}\n\nBitte korrigieren Sie die Zeiten.`)
                          e.target.focus()
                          return
                        }
                      }
                    }
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  placeholder="HH:MM (z.B. 18:00, 22:30)"
                  title="24-Stunden Format: HH:MM (z.B. 18:00, 22:30)"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Ziel *</label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  placeholder="z.B. FC Basel Stadion"
                  required
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#374151'
                }}>Fahrer *</label>
                <input
                  type="text"
                  value={formData.driver}
                  onChange={(e) => setFormData(prev => ({ ...prev, driver: e.target.value }))}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  placeholder="Name des Fahrers"
                  required
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '6px',
                color: '#374151'
              }}>Kontakt-Telefon *</label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
                placeholder="+41 79 123 45 67"
                required
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#1B7E3A'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '6px',
                color: '#374151'
              }}>Bemerkungen</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
                rows={3}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#1B7E3A'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27, 126, 58, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }} 
            className="sm:flex-row">
              <button 
                type="button" 
                onClick={() => setShowForm(false)} 
                style={{
                  flex: '1',
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb'
                  e.currentTarget.style.borderColor = '#9ca3af'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.borderColor = '#d1d5db'
                }}
              >
                Abbrechen
              </button>
              <button 
                type="submit" 
                style={{
                  flex: '1',
                  padding: '12px 24px',
                  backgroundColor: (loading || isSubmitting) ? '#9ca3af' : '#1B7E3A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  position: 'relative',
                  overflow: 'hidden',
                  transform: isSubmitting ? 'scale(0.98)' : 'scale(1)',
                  boxShadow: isSubmitting 
                    ? '0 2px 4px rgba(0, 0, 0, 0.1)' 
                    : '0 4px 8px rgba(27, 126, 58, 0.2)'
                }}
                disabled={loading || isSubmitting}
                onMouseEnter={(e) => {
                  if (!loading && !isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#166534'
                    e.currentTarget.style.transform = 'scale(1.02)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && !isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#1B7E3A'
                    e.currentTarget.style.transform = 'scale(1)'
                  }
                }}
                onMouseDown={(e) => {
                  if (!loading && !isSubmitting) {
                    e.currentTarget.style.transform = 'scale(0.98)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseUp={(e) => {
                  if (!loading && !isSubmitting) {
                    setTimeout(() => {
                      e.currentTarget.style.transform = 'scale(1.02)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(27, 126, 58, 0.2)'
                    }, 100)
                  }
                }}
              >
                {/* Click ripple effect overlay */}
                {isSubmitting && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translate(-50%, -50%) scale(0)',
                    animation: 'ripple 0.6s ease-out',
                    pointerEvents: 'none'
                  }} />
                )}
                
                {/* Loading spinner */}
                {(loading || isSubmitting) && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                
                {(loading || isSubmitting) ? 'Erstelle...' : 'Reservierung erstellen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #1B7E3A',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6b7280' }}>Reservierungen werden geladen...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <p style={{ fontWeight: '500', marginBottom: '12px' }}>
            Fehler beim Laden der Reservierungen: {error}
          </p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <button 
              onClick={() => {
                console.log('Retrying reservation fetch...')
                fetchReservations()
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#b91c1c'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626'
              }}
            >
              <RefreshCw size={16} />
              Erneut versuchen
            </button>
            <button 
              onClick={() => {
                console.log('Debug info:')
                console.log('isAuthenticated:', isAuthenticated())
                console.log('profile:', profile)
                if (typeof window !== 'undefined' && (window as any).debugAuth) {
                  (window as any).debugAuth()
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4b5563'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6b7280'
              }}
            >
              Debug-Info anzeigen
            </button>
          </div>
        </div>
      )}

      {/* Reservations List Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#111827',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Bus size={24} />
          Bus-Reservierungen
        </h2>
        
        {/* Current and Future Reservations */}
        <div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Calendar size={20} />
            Aktuelle & Bevorstehende Reservierungen
          </h3>
          {currentAndFutureReservations.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 20px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <Bus size={64} style={{ color: '#9ca3af', margin: '0 auto 16px' }} />
              <h4 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>Keine aktuellen Reservierungen</h4>
              <p style={{ color: '#6b7280' }}>
                Erstelle deine erste Bus-Reservierung mit dem Button oben.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {currentAndFutureReservations.map(reservation => (
                <div 
                  key={reservation.id} 
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                    padding: '24px',
                    position: 'relative',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  {/* Header with Team, Purpose and Actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <h4 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#111827',
                        marginBottom: '4px'
                      }}>{reservation.team}</h4>
                      <p style={{
                        color: '#6b7280',
                        fontSize: '16px',
                        fontWeight: '500'
                      }}>{reservation.purpose}</p>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        ...(reservation.status === 'approved' ? {
                          backgroundColor: '#dcfce7',
                          color: '#166534'
                        } : reservation.status === 'rejected' ? {
                          backgroundColor: '#fee2e2',
                          color: '#dc2626'
                        } : {
                          backgroundColor: '#fef3c7',
                          color: '#d97706'
                        })
                      }}>
                        {reservation.status === 'approved' ? 'Genehmigt' : 
                         reservation.status === 'rejected' ? 'Abgelehnt' : 
                         'Ausstehend'}
                      </span>
                      <button
                        onClick={() => handleDeleteReservation(reservation.id)}
                        disabled={deletingId === reservation.id}
                        style={{
                          padding: '8px',
                          backgroundColor: deletingId === reservation.id ? '#f3f4f6' : '#fee2e2',
                          color: deletingId === reservation.id ? '#9ca3af' : '#dc2626',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: deletingId === reservation.id ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          if (deletingId !== reservation.id) {
                            e.currentTarget.style.backgroundColor = '#fecaca'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (deletingId !== reservation.id) {
                            e.currentTarget.style.backgroundColor = '#fee2e2'
                          }
                        }}
                        title="Reservierung löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Main Information Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '1px solid #bbf7d0'
                      }}>
                        <Calendar size={20} style={{ color: '#16a34a', flexShrink: 0 }} />
                        <div>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#16a34a',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>Datum</div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#111827'
                          }}>
                            {new Date(reservation.date).toLocaleDateString('de-CH', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#eff6ff',
                        borderRadius: '8px',
                        border: '1px solid #bfdbfe'
                      }}>
                        <Clock size={20} style={{ color: '#2563eb', flexShrink: 0 }} />
                        <div>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#2563eb',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>Zeiten</div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#111827'
                          }}>
                            {reservation.startTime} - {reservation.endTime || 'offen'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#fef7ff',
                        borderRadius: '8px',
                        border: '1px solid #e9d5ff'
                      }}>
                        <MapPin size={20} style={{ color: '#9333ea', flexShrink: 0 }} />
                        <div>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#9333ea',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>Ziel</div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#111827'
                          }}>{reservation.destination}</div>
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#fffbeb',
                        borderRadius: '8px',
                        border: '1px solid #fed7aa'
                      }}>
                        <User size={20} style={{ color: '#ea580c', flexShrink: 0 }} />
                        <div>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#ea580c',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>Fahrer</div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#111827'
                          }}>{reservation.driver}</div>
                          <div style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            fontWeight: '500'
                          }}>{reservation.contactPhone}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes Section */}
                  {reservation.notes && reservation.notes !== '-' && (
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '8px'
                      }}>Bemerkungen</div>
                      <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        lineHeight: '1.5',
                        margin: '0',
                        padding: '8px 12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb'
                      }}>{reservation.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Reservations Section */}
        {pastReservations.length > 0 && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <History size={20} />
                Vergangene Reservierungen
              </h3>
              <button
                onClick={() => setShowPastReservations(!showPastReservations)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: showPastReservations ? '#f3f4f6' : 'transparent',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showPastReservations ? '#e5e7eb' : '#f9fafb'
                  e.currentTarget.style.borderColor = '#9ca3af'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showPastReservations ? '#f3f4f6' : 'transparent'
                  e.currentTarget.style.borderColor = '#d1d5db'
                }}
              >
                <History size={16} />
                {showPastReservations ? 'Ausblenden' : `${pastReservations.length} vergangene anzeigen`}
              </button>
            </div>

            {showPastReservations && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {pastReservations.map(reservation => (
                  <div 
                    key={reservation.id} 
                    style={{
                      backgroundColor: '#f9fafb',
                      borderRadius: '16px',
                      border: '1px solid #e5e7eb',
                      padding: '24px',
                      opacity: '0.8',
                      position: 'relative'
                    }}
                  >
                    {/* Header with Team, Purpose and Actions */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <h4 style={{
                          fontSize: '20px',
                          fontWeight: '700',
                          color: '#111827',
                          marginBottom: '4px'
                        }}>{reservation.team}</h4>
                        <p style={{
                          color: '#6b7280',
                          fontSize: '16px',
                          fontWeight: '500'
                        }}>{reservation.purpose}</p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{
                          padding: '6px 12px',
                          backgroundColor: '#e5e7eb',
                          color: '#6b7280',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Vergangen
                        </span>
                        <span style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          ...(reservation.status === 'approved' ? {
                            backgroundColor: '#dcfce7',
                            color: '#166534'
                          } : reservation.status === 'rejected' ? {
                            backgroundColor: '#fee2e2',
                            color: '#dc2626'
                          } : {
                            backgroundColor: '#fef3c7',
                            color: '#d97706'
                          })
                        }}>
                          {reservation.status === 'approved' ? 'Genehmigt' : 
                           reservation.status === 'rejected' ? 'Abgelehnt' : 
                           'Ausstehend'}
                        </span>
                        <button
                          onClick={() => handleDeleteReservation(reservation.id)}
                          disabled={deletingId === reservation.id}
                          style={{
                            padding: '8px',
                            backgroundColor: deletingId === reservation.id ? '#f3f4f6' : '#fee2e2',
                            color: deletingId === reservation.id ? '#9ca3af' : '#dc2626',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: deletingId === reservation.id ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            if (deletingId !== reservation.id) {
                              e.currentTarget.style.backgroundColor = '#fecaca'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (deletingId !== reservation.id) {
                              e.currentTarget.style.backgroundColor = '#fee2e2'
                            }
                          }}
                          title="Reservierung löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Main Information Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: '20px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#f0f9ff',
                          borderRadius: '8px',
                          border: '1px solid #e0f2fe'
                        }}>
                          <Calendar size={20} style={{ color: '#0284c7', flexShrink: 0 }} />
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#0284c7',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Datum</div>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#111827'
                            }}>
                              {new Date(reservation.date).toLocaleDateString('de-CH', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <Clock size={20} style={{ color: '#64748b', flexShrink: 0 }} />
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#64748b',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Zeiten</div>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#111827'
                            }}>
                              {reservation.startTime} - {reservation.endTime || 'offen'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#faf5ff',
                          borderRadius: '8px',
                          border: '1px solid #e9d5ff'
                        }}>
                          <MapPin size={20} style={{ color: '#9333ea', flexShrink: 0 }} />
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#9333ea',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Ziel</div>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#111827'
                            }}>{reservation.destination}</div>
                          </div>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#fefbf0',
                          borderRadius: '8px',
                          border: '1px solid #fed7aa'
                        }}>
                          <User size={20} style={{ color: '#ea580c', flexShrink: 0 }} />
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#ea580c',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Fahrer</div>
                            <div style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#111827'
                            }}>{reservation.driver}</div>
                            <div style={{
                              fontSize: '14px',
                              color: '#6b7280',
                              fontWeight: '500'
                            }}>{reservation.contactPhone}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Notes Section */}
                    {reservation.notes && reservation.notes !== '-' && (
                      <div style={{
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '8px'
                        }}>Bemerkungen</div>
                        <p style={{
                          fontSize: '14px',
                          color: '#374151',
                          lineHeight: '1.5',
                          margin: '0',
                          padding: '8px 12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>{reservation.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      </div>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes ripple {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(4);
            opacity: 0;
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  )
}
